import { getStatusDisplayText } from "../../utils/helpers";
import { Button } from "../Button";
import type { RoomInfo } from "../../types/game";

interface RoomCardProps {
  room: RoomInfo;
  canRejoin: boolean;
  onRejoin: () => void;
  onJoin: () => void;
  onSpectate: () => void;
  advancedMode: boolean;
  isAdmin?: boolean;
  onDeleteRoom?: () => void;
}

// Map game IDs to emoji icons (only enabled games)
const GAME_ICONS: Record<string, string> = {
  "terrible-psychiatrist": "🧠",
  "peer-review": "⭐",
  icebreaker: "❄️",
  "sick-day": "🤒",
};

export function RoomCard({
  room,
  canRejoin,
  onRejoin,
  onJoin,
  onSpectate,
  advancedMode,
  isAdmin,
  onDeleteRoom,
}: RoomCardProps) {
  const gameIcon = GAME_ICONS[room.game_id] || "🎮";
  const isFull = room.num_players >= room.max_players;
  const isActive = room.is_started;

  // Get game display name
  const getGameName = (): string => {
    if (!room.game_id) {
      return "Unknown Game";
    }
    if (room.game_id === "terrible-psychiatrist") {
      return "Terrible Psychiatrist";
    }
    return room.game_id
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div
      className="glass rounded-lg overflow-hidden border-2 hover:shadow-lg transition-all"
      style={{
        borderColor: isActive ? "var(--color-success)" : "var(--color-accent)",
      }}
      data-testid={`room-card-${room.session_id}`}
    >
      {/* Active indicator - colored top bar */}
      {isActive && (
        <div
          className="h-1"
          style={{ backgroundColor: "var(--color-success)" }}
          data-testid="room-active-indicator"
        />
      )}

      {/* Header */}
      <div
        className="px-4 py-3 border-b"
        style={{
          backgroundColor: "var(--color-surface-hover)",
          borderColor: "var(--color-accent)",
        }}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl" data-testid="room-game-icon">
                {gameIcon}
              </span>
              <h3
                className="font-semibold text-lg"
                style={{ color: "var(--color-text-primary)" }}
                data-testid="room-game-title"
              >
                {getGameName()}
              </h3>
            </div>
            <p
              className="text-xs ml-9"
              style={{ color: "var(--color-text-secondary)" }}
              data-testid="room-host-name"
            >
              Hosted by: {room.host_name}
            </p>
          </div>
          <div className="flex flex-col gap-1 items-end">
            {!room.is_public && (
              <span
                className="text-xl"
                data-testid="room-status-private"
                title="Private room"
              >
                🔒
              </span>
            )}
            {isFull && (
              <span
                className="px-2 py-1 rounded text-xs font-medium"
                style={{
                  backgroundColor: "var(--color-error)",
                  color: "var(--color-surface)",
                }}
                data-testid="room-status-full"
              >
                Full
              </span>
            )}

            {/* Admin trash button - only visible in advanced mode */}
            {advancedMode && isAdmin && onDeleteRoom && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    window.confirm(
                      `Delete room "${getGameName()}"?\n\nAll players will be kicked to the home screen.`,
                    )
                  ) {
                    onDeleteRoom();
                  }
                }}
                className="px-2 py-1 rounded text-xs font-medium transition-colors hover:brightness-110"
                style={{
                  backgroundColor: "var(--color-error)",
                  color: "var(--color-surface)",
                }}
                title="Delete session (Admin only)"
                data-testid="room-delete-button"
              >
                🗑️ Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-4 space-y-3">
        {/* Metadata Grid */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div
            className="rounded p-2"
            style={{ backgroundColor: "var(--color-surface-hover)" }}
          >
            <p
              className="text-xs mb-0.5"
              style={{ color: "var(--color-text-secondary)" }}
            >
              👥 Players
            </p>
            <p
              className="font-medium"
              style={{ color: "var(--color-primary)" }}
              data-testid="room-player-count"
            >
              {room.num_players}/{room.max_players}
            </p>
          </div>
          {isActive && (
            <div
              className="rounded p-2"
              style={{ backgroundColor: "var(--color-surface-hover)" }}
            >
              <p
                className="text-xs mb-0.5"
                style={{ color: "var(--color-text-secondary)" }}
              >
                📊 Round
              </p>
              <p
                className="font-medium"
                style={{ color: "var(--color-primary)" }}
                data-testid="room-round"
              >
                {room.current_round}
              </p>
            </div>
          )}
          {!isActive && (
            <div
              className="rounded p-2"
              style={{ backgroundColor: "var(--color-surface-hover)" }}
            >
              <p
                className="text-xs mb-0.5"
                style={{ color: "var(--color-text-secondary)" }}
              >
                📋 Status
              </p>
              <p
                className="font-medium"
                style={{ color: "var(--color-primary)" }}
                data-testid="room-status"
              >
                {getStatusDisplayText(room.status)}
              </p>
            </div>
          )}
        </div>

        {/* Technical Details - Shown only in Advanced Mode (toggle with "^" key) */}
        {advancedMode && (
          <div
            className="rounded p-2 space-y-2"
            style={{ backgroundColor: "var(--color-surface-hover)" }}
            data-testid="room-technical-details"
          >
            <div
              className="text-xs font-bold mb-2"
              style={{ color: "var(--color-text-secondary)" }}
            >
              🔧 Technical Details
            </div>
            <div>
              <p
                className="text-xs mb-0.5"
                style={{ color: "var(--color-text-secondary)" }}
              >
                Session ID
              </p>
              <code
                className="text-xs font-mono break-all"
                style={{ color: "var(--color-primary)" }}
              >
                {room.session_id}
              </code>
            </div>
            <div>
              <p
                className="text-xs mb-0.5"
                style={{ color: "var(--color-text-secondary)" }}
              >
                Game ID
              </p>
              <code
                className="text-xs font-mono"
                style={{ color: "var(--color-primary)" }}
              >
                {room.game_id}
              </code>
            </div>
            <div>
              <p
                className="text-xs mb-0.5"
                style={{ color: "var(--color-text-secondary)" }}
              >
                Player IDs
              </p>
              <code
                className="text-xs font-mono"
                style={{ color: "var(--color-primary)" }}
              >
                {room.player_ids?.length ?? 0} registered
              </code>
            </div>
          </div>
        )}
      </div>

      {/* Footer - Action Button */}
      <div
        className="px-4 py-3 border-t"
        style={{
          borderColor: "var(--color-accent)",
          backgroundColor: "var(--color-surface-hover)",
        }}
      >
        {canRejoin ? (
          <Button
            onClick={onRejoin}
            variant="success"
            className="w-full text-sm"
            data-testid="room-rejoin-button"
          >
            🔄 Rejoin Game
          </Button>
        ) : isActive ? (
          <Button
            onClick={onSpectate}
            variant="secondary"
            className="w-full text-sm"
            data-testid="room-spectate-button"
          >
            👁️ Watch Game In-Progress
          </Button>
        ) : isFull ? (
          <Button
            disabled
            className="w-full text-sm opacity-50 cursor-not-allowed"
            data-testid="room-full-button"
          >
            🔴 Room Full
          </Button>
        ) : (
          <Button
            onClick={onJoin}
            variant="primary"
            className="w-full text-sm"
            data-testid="room-join-button"
          >
            ➕ Join Room
          </Button>
        )}
      </div>
    </div>
  );
}
